/**
 * LaunchPopup — the Home-screen pop-up that introduces a launch, e.g. the
 * Signature Drinks (Business Rule #64), and sends the customer to one
 * destination (the same link shapes a banner uses).
 *
 * The server (`GET /api/popups/active`) decides which pop-up applies at this
 * store and, for a signed-in customer, enforces the caps across all their
 * phones. This component adds what only the phone knows:
 * - the same caps from this device's storage (guests have nothing else, and it
 *   keeps a signed-in customer's pre-login sightings counted),
 * - the destination must have something in stock at this store (so the button
 *   never opens an empty page) — read from the Home menu already loaded,
 * - one pop-up per app open: it waits 1.5 s and stands aside when the order
 *   ready / renew pass / rating pop-ups or another dialog are up
 *   (`utils/homePopups.js` + the `blocked` prop).
 * The "card" style shows the destination's first drinks, in stock, from the
 * live menu; the "image" style shows the uploaded creative and the button.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, X } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './ui/dialog';
import OptimizedImage from './OptimizedImage';
import { itemsInCategory } from '../utils/menuSections';
import { followBannerLink } from '../utils/bannerLink';
import { onHomePopupOpen, otherHomePopupShown, reportHomePopup } from '../utils/homePopups';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOW_DELAY_MS = 1500;
const THUMBS = 3;
const DAY_MS = 24 * 60 * 60 * 1000;
const POPUP_KEY = 'launch';

// Set once the pop-up showed, or another Home pop-up took this app open.
let decidedThisOpen = false;

const storageKey = (id) => `pbc_popup_${id}`;

/**
 * This device's record of one pop-up: `{shows, last, stopped}`.
 * @param {string} id
 * @returns {{shows?: number, last?: number, stopped?: boolean}}
 */
export function readDeviceState(id) {
  try { return JSON.parse(localStorage.getItem(storageKey(id)) || 'null') || {}; } catch (e) { return {}; }
}

function writeDeviceState(id, patch) {
  try { localStorage.setItem(storageKey(id), JSON.stringify({ ...readDeviceState(id), ...patch })); } catch (e) { /* storage off — the server caps still hold for signed-in customers */ }
}

/**
 * Whether this device's record still allows a show: not stopped, under
 * `max_shows`, and `cooldown_days` past the last show.
 * @param {{shows?: number, last?: number, stopped?: boolean}} state
 * @param {{max_shows: number, cooldown_days: number}} popup
 * @param {number} now - ms timestamp
 * @returns {boolean}
 */
export function deviceAllows(state, popup, now) {
  if (state.stopped) return false;
  if ((state.shows || 0) >= (popup.max_shows || 0)) return false;
  if (state.last && now - state.last < (popup.cooldown_days || 0) * DAY_MS) return false;
  return true;
}

/**
 * The price a thumb shows: the per-item discount applied (Business Rule #10)
 * with the Menu card's rounding, so the pop-up and the Menu list agree.
 * @param {{price?: number, item_discount?: {discount_type: string, discount_value: number}}} item
 * @returns {{price: number, was: number|null}} `was` is the struck-through full price, null without a discount
 */
export function displayPrice(item) {
  const full = Math.round(item?.price || 0);
  const d = item?.item_discount;
  if (!d || !d.discount_type || !(d.discount_value > 0)) return { price: full, was: null };
  const price = d.discount_type === 'percentage'
    ? Math.round(full * (1 - d.discount_value / 100))
    : Math.max(0, Math.round(full - d.discount_value));
  return price < full ? { price, was: full } : { price: full, was: null };
}

/**
 * The in-stock menu items a pop-up promotes at this store, in the order the
 * Menu lists them; `null` when its destination is not a category or an item
 * (an offer, a pass, a whole page), which has no stock to check.
 * @param {{link?: {type?: string, target?: string}}} popup
 * @param {Object[]} menuItems - this store's menu
 * @returns {Object[]|null}
 */
export function promotedItems(popup, menuItems) {
  const { type, target } = popup?.link || {};
  const live = (menuItems || []).filter((i) => i.available !== false);
  if (type === 'category') return itemsInCategory(live, target);
  if (type === 'menu_item') return live.filter((i) => i.name === target);
  return null;
}

/**
 * The small line above the headline with `{store}` filled in.
 * @param {string} kicker
 * @param {{name?: string}} branch
 * @returns {string}
 */
export function kickerText(kicker, branch) {
  const store = (branch?.name || '').trim() || 'Penguin Bean';
  return (kicker || '').replace(/\{store\}/g, store).trim();
}

const authHeaders = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

export default function LaunchPopup({ token, branch, menuItems = [], blocked = false }) {
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const blockedRef = useRef(blocked);
  const menuRef = useRef(menuItems);
  const openRef = useRef(false);
  blockedRef.current = blocked;
  menuRef.current = menuItems;
  openRef.current = open;

  // The Home menu must be this store's before the stock check means anything.
  const menuBranch = menuItems.length ? (menuItems[0].branch_id || branch?.id) : null;
  const menuReady = !!branch?.id && menuBranch === branch?.id;

  useEffect(() => {
    if (decidedThisOpen || !menuReady) return undefined;
    let cancelled = false;
    let timer = null;
    (async () => {
      let p;
      try {
        const res = await axios.get(`${API}/popups/active`, {
          params: { branch_id: branch.id },
          headers: authHeaders(token),
        });
        p = res.data;
      } catch (e) {
        return; // non-essential surface
      }
      if (cancelled || !p?.id) return;
      const device = readDeviceState(p.id);
      if (!deviceAllows(device, p, Date.now())) return;
      const promoted = promotedItems(p, menuRef.current);
      if (promoted && promoted.length === 0) return;
      timer = setTimeout(() => {
        if (cancelled || decidedThisOpen) return;
        decidedThisOpen = true;
        if (blockedRef.current || otherHomePopupShown(POPUP_KEY)) return;
        setPopup(p);
        setItems(promoted || []);
        setOpen(true);
        reportHomePopup(POPUP_KEY, true);
        writeDeviceState(p.id, { shows: (device.shows || 0) + 1, last: Date.now() });
        axios.post(`${API}/popups/${p.id}/shown`, { first_on_device: !device.shows }, { headers: authHeaders(token) }).catch(() => {});
      }, SHOW_DELAY_MS);
    })();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [menuReady, branch?.id, token]);

  // Stand aside when another Home pop-up or dialog opens on top.
  useEffect(() => onHomePopupOpen((key) => {
    if (key !== POPUP_KEY && openRef.current) { setOpen(false); reportHomePopup(POPUP_KEY, false); }
  }), []);
  useEffect(() => {
    if (blocked && openRef.current) { setOpen(false); reportHomePopup(POPUP_KEY, false); }
  }, [blocked]);

  if (!popup) return null;

  const close = () => {
    setOpen(false);
    reportHomePopup(POPUP_KEY, false);
    axios.post(`${API}/popups/${popup.id}/dismiss`, null, { headers: authHeaders(token) }).catch(() => {});
  };

  const explore = () => {
    setOpen(false);
    reportHomePopup(POPUP_KEY, false);
    if (popup.stop_on_tap) writeDeviceState(popup.id, { stopped: true });
    axios.post(`${API}/popups/${popup.id}/tap`, null, { headers: authHeaders(token) }).catch(() => {});
    followBannerLink(popup.link, navigate);
  };

  const card = popup.style !== 'image';
  const thumbs = items.slice(0, THUMBS);
  const more = items.length - thumbs.length;
  const kicker = kickerText(popup.kicker, branch);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) close(); }}>
      <DialogContent
        className="w-[calc(100%-44px)] max-w-[346px] gap-0 overflow-hidden rounded-[22px] border-0 p-0 sm:rounded-[22px] [&>button:last-child]:hidden"
        data-testid="launch-popup"
      >
        <DialogTitle className="sr-only">{popup.headline || popup.button_text}</DialogTitle>
        <DialogDescription className="sr-only">{popup.subline || kicker}</DialogDescription>

        <div className="relative aspect-video bg-[hsl(20_45%_40%)]">
          <OptimizedImage
            src={popup.image_url}
            imageUrls={popup.image_urls}
            size="medium"
            alt={popup.headline || ''}
            width={960}
            height={540}
            eager
            fallbackIcon={false}
            className="h-full w-full object-cover"
          />
          {card && (
            <span className="absolute left-3.5 top-3.5 rounded-full bg-accent px-2.5 py-1 text-[11px] font-extrabold tracking-[0.1em] text-accent-foreground">
              NEW
            </span>
          )}
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full bg-white/90 text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid="launch-popup-close"
          >
            <X className="h-4 w-4" strokeWidth={2.2} />
          </button>
        </div>

        <div className={`grid px-5 pb-3 ${card ? 'pt-[18px]' : 'pt-3.5'}`}>
          {card && (
            <>
              {kicker && <p className="text-[11.5px] font-bold uppercase tracking-[0.12em] text-accent">{kicker}</p>}
              <h3 className="mt-0.5 font-fraunces text-[25px] font-bold leading-[1.12] tracking-tight">{popup.headline}</h3>
              {popup.subline && <p className="mt-1 text-sm leading-snug text-muted-foreground">{popup.subline}</p>}
              {thumbs.length > 0 && (
                <div className="mt-3 grid grid-cols-4 gap-2">
                  {thumbs.map((item) => {
                    const { price, was } = displayPrice(item);
                    return (
                    <div key={item.id} className="grid min-w-0 content-start gap-1">
                      <div className="aspect-square overflow-hidden rounded-xl bg-muted">
                        <OptimizedImage src={item.image_url} imageUrls={item.image_urls} size="thumb" alt="" width={140} height={140} eager className="h-full w-full object-cover" />
                      </div>
                      <span className="line-clamp-2 text-[11px] font-semibold leading-tight">{item.name}</span>
                      {was === null ? (
                        <span className="text-[11.5px] font-extrabold">₹{price}</span>
                      ) : (
                        <span className="flex items-baseline gap-1">
                          <span className="text-[11.5px] font-extrabold text-green-600">₹{price}</span>
                          <span className="text-[10px] text-muted-foreground line-through">₹{was}</span>
                        </span>
                      )}
                    </div>
                    );
                  })}
                  {more > 0 && (
                    <div className="grid min-w-0 content-start gap-1">
                      <div className="grid aspect-square place-items-center rounded-xl border border-border text-[17px] font-extrabold">+{more}</div>
                      <span className="text-[11px] font-semibold leading-tight">more {more === 1 ? 'drink' : 'drinks'}</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          <button
            type="button"
            onClick={explore}
            className={`${card ? 'mt-4' : ''} flex h-[52px] items-center justify-center gap-2 rounded-[14px] bg-primary text-base font-bold text-primary-foreground transition-opacity active:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2`}
            data-testid="launch-popup-cta"
          >
            {popup.button_text}
            <ArrowRight className="h-[18px] w-[18px] text-accent" strokeWidth={2.4} />
          </button>
          <button
            type="button"
            onClick={close}
            className="h-10 text-sm font-semibold text-muted-foreground focus:outline-none focus-visible:underline"
            data-testid="launch-popup-later"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
